import axios from 'axios'

const API_URL =
  import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8000' : '')

if (!API_URL && import.meta.env.PROD) {
  console.error('FATAL: VITE_API_URL environment variable is not set')
}

const api = axios.create({
  baseURL: `${API_URL}/api/v1/portal`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * In-memory token store. Kept out of URLs and localStorage to
 * minimise leakage surface (browser history, Referer headers, logs).
 */
const PORTAL_TOKEN_KEY = 'lipaflow.portal.token'
let _portalToken: string | null = null

function readSessionToken(): string | null {
  try {
    return window.sessionStorage.getItem(PORTAL_TOKEN_KEY)
  } catch {
    return null
  }
}

function writeSessionToken(token: string | null): void {
  try {
    if (!token) {
      window.sessionStorage.removeItem(PORTAL_TOKEN_KEY)
      return
    }
    window.sessionStorage.setItem(PORTAL_TOKEN_KEY, token)
  } catch {
    // Ignore storage failures and continue with in-memory fallback.
  }
}

// Restore token on refresh (same-tab/session only).
_portalToken = readSessionToken()
if (_portalToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${_portalToken}`
}

/**
 * Use a portal token for API calls. A signed-in session is also kept for the
 * tab (``persist``); a payment link's narrower session is not, so opening a
 * link never replaces the payer's own sign-in.
 */
export function setPortalToken(token: string, { persist = true }: { persist?: boolean } = {}) {
  _portalToken = token
  if (persist) writeSessionToken(token)
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

/** Go back to the tab's stored sign-in (or none), e.g. on leaving a payment link. */
export function restoreSessionToken() {
  _portalToken = readSessionToken()
  if (_portalToken) api.defaults.headers.common['Authorization'] = `Bearer ${_portalToken}`
  else delete api.defaults.headers.common['Authorization']
}

/** Return the currently-stored token (or null). */
export function getPortalToken(): string | null {
  return _portalToken
}

/** Remove in-memory portal token and Authorization header. */
export function clearPortalToken() {
  _portalToken = null
  writeSessionToken(null)
  delete api.defaults.headers.common['Authorization']
}

export default api
